import type { ErrorEvent as ObsrviqErrorEvent } from '@logclip/types';
import type { InstrumentCtx, Teardown } from './context.js';
import { errorToConsoleEvent } from './console.js';
import { maskString } from './mask.js';
import { parseSource } from './serialize.js';
import { uuid } from './util.js';

export function instrumentErrors(ctx: InstrumentCtx): Teardown {
  function emitError(args: {
    name: string;
    message: string;
    stack?: string;
    source?: { url?: string; line?: number; col?: number };
    handled: boolean;
  }) {
    const e: ObsrviqErrorEvent = {
      id: uuid(),
      sessionId: ctx.sessionId,
      type: 'error',
      t: ctx.clock.now(),
      ts: ctx.clock.wall(),
      name: args.name,
      message: maskString(args.message),
      stack: args.stack ? maskString(args.stack) : undefined,
      source: args.source,
      handled: args.handled,
    };
    ctx.emit(e);
    // Surface uncaught errors in the Console panel too (§9 C-6).
    ctx.emit(errorToConsoleEvent(ctx, e));
  }

  const onError = (ev: ErrorEvent) => {
    // A failed resource load (img/script/link/media) ALSO fires an 'error' event in the
    // capture phase — but with the failing element as `target` and no Error object. That
    // isn't a JS exception (asset failures are already reported by network capture), and
    // if we don't skip it every 404'd image floods the error stream as a stackless
    // "Unknown error". Genuine window errors have `target === window`.
    if (ev.target && ev.target !== window) return;
    const err = ev.error as Error | undefined;
    // Cross-origin script errors are opaque ("Script error.", no error object, no stack).
    // Keep them but label them so they group as one bucket instead of masquerading as a
    // generic error — the fix on the site is `crossorigin` on the script tag + CORS.
    const crossOrigin = !err && (ev.message === 'Script error.' || ev.message === 'Script error');
    emitError({
      name: err?.name || (crossOrigin ? 'CrossOriginScriptError' : 'Error'),
      message: err?.message || ev.message || 'Unknown error',
      stack: err?.stack,
      source: { url: ev.filename, line: ev.lineno, col: ev.colno },
      handled: false,
    });
  };

  const onRejection = (ev: PromiseRejectionEvent) => {
    const reason = ev.reason;
    if (reason instanceof Error) {
      emitError({
        name: reason.name,
        message: reason.message,
        stack: reason.stack,
        source: parseSource(reason.stack),
        handled: false,
      });
    } else {
      emitError({
        name: 'UnhandledRejection',
        message: typeof reason === 'string' ? reason : safeStringify(reason),
        handled: false,
      });
    }
  };

  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onRejection, true);

  return () => {
    window.removeEventListener('error', onError, true);
    window.removeEventListener('unhandledrejection', onRejection, true);
  };
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}
